import React, { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import moment from "moment";
import { BASE_URL } from "../../../../data/BaseUrl";
import "../../../../styles/trainer-report/headcount.css";
import "../../../../styles/trainer-report/deployment-matrix.css";

const MAX_FETCH_LIMIT = 9999;
const IDEAL_TRAINERS = 4;
const IDEAL_RECEPTIONISTS = 2;

const buildWindows = (year, month) => {
  const daysInMonth = moment(
    `${year}-${String(month).padStart(2, "0")}`,
    "YYYY-MM"
  ).daysInMonth();
  const windows = [];
  let start = 1;
  while (start <= daysInMonth) {
    windows.push({ start, end: Math.min(start + 6, daysInMonth) });
    start += 7;
  }
  return windows;
};

const getWindowDates = (year, month, winStart, winEnd) => {
  const dates = [];
  for (let d = winStart; d <= winEnd; d++) {
    dates.push(
      moment(
        `${year}-${String(month).padStart(2, "0")}-${String(d).padStart(2, "0")}`,
        "YYYY-MM-DD"
      ).format("YYYY-MM-DD")
    );
  }
  return dates;
};

/** Green = ideal met, yellow = partial, red = nobody present */
const getCellStatus = (trainersPresent, receptPresent) => {
  if (trainersPresent >= IDEAL_TRAINERS && receptPresent >= IDEAL_RECEPTIONISTS)
    return "green";
  if (trainersPresent + receptPresent > 0) return "yellow";
  return "red";
};

// ---------------------------------------------------------------------------
// Drill-down section for one role (trainers or receptionists)
// ---------------------------------------------------------------------------
const DrillSection = ({ title, ideal, data }) => (
  <div className="shc-drill__section">
    <div className="shc-drill__section-title">
      {title}
      <span
        className={`shc-drill__badge shc-drill__badge--${
          data.present.length >= ideal ? "green" : "red"
        }`}
      >
        {data.present.length}/{ideal}
      </span>
    </div>
    {data.present.map((p) => (
      <div key={p.user_id} className="shc-drill__row shc-drill__row--present">
        <span className="shc-drill__icon">✓</span>
        <span className="shc-drill__name">{p.name}</span>
        {p.checkin_time && (
          <span className="shc-drill__time">{p.checkin_time}</span>
        )}
      </div>
    ))}
    {data.absent.map((p) => (
      <div key={p.user_id} className="shc-drill__row shc-drill__row--absent">
        <span className="shc-drill__icon">✗</span>
        <span className="shc-drill__name">{p.name}</span>
      </div>
    ))}
    {data.dayOff.map((p) => (
      <div key={p.user_id} className="shc-drill__row shc-drill__row--dayoff">
        <span className="shc-drill__icon">○</span>
        <span className="shc-drill__name">{p.name}</span>
        <span className="shc-drill__time">Off</span>
      </div>
    ))}
    {data.present.length === 0 &&
      data.absent.length === 0 &&
      data.dayOff.length === 0 && (
        <div className="shc-drill__empty">No staff data</div>
      )}
  </div>
);

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
const HeadcountTab = ({ gymFilter }) => {
  const now = moment();
  const [year, setYear] = useState(now.year());
  const [month, setMonth] = useState(now.month() + 1); // 1-based
  const [windowIdx, setWindowIdx] = useState(0);
  const [dayData, setDayData] = useState([]); // [{ date, records }]
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reloadTrigger, setReloadTrigger] = useState(0);
  const [activeCell, setActiveCell] = useState(null); // { gymId, date, gymName }

  const token = useSelector((state) => state.auth?.user?.token);

  const windows = useMemo(() => buildWindows(year, month), [year, month]);

  // Clamp windowIdx when month changes
  useEffect(() => {
    setWindowIdx((i) => Math.min(i, windows.length - 1));
    setActiveCell(null);
  }, [year, month, windows.length]);

  // Jump to the window containing today on mount
  useEffect(() => {
    const todayMoment = moment();
    if (
      todayMoment.year() === year &&
      todayMoment.month() + 1 === month
    ) {
      const dayOfMonth = todayMoment.date();
      const idx = windows.findIndex(
        (w) => w.start <= dayOfMonth && w.end >= dayOfMonth
      );
      if (idx >= 0) setWindowIdx(idx);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // run once on mount

  const currentWindow = windows[windowIdx] || windows[0];
  const windowDates = useMemo(
    () =>
      currentWindow
        ? getWindowDates(year, month, currentWindow.start, currentWindow.end)
        : [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [year, month, currentWindow?.start, currentWindow?.end]
  );

  const dateFrom = windowDates[0] || "";
  const dateTo = windowDates[windowDates.length - 1] || "";

  // Parallel per-day fetch (one call per date so processStaffDetails is accurate)
  useEffect(() => {
    if (!token || windowDates.length === 0) return;
    const controller = new AbortController();

    const fetchDay = async (date) => {
      const params = new URLSearchParams({
        date_from: date,
        date_to: date,
        limit: MAX_FETCH_LIMIT,
        page: 1,
      });
      if (gymFilter?.value) params.set("gym_id", gymFilter.value);
      const res = await fetch(`${BASE_URL}/staff/report/v3?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
        signal: controller.signal,
      });
      if (!res.ok) throw new Error(`Server error: ${res.status}`);
      const json = await res.json();
      const records = Array.isArray(json.data)
        ? json.data
        : Array.isArray(json.result)
        ? json.result
        : Array.isArray(json.records)
        ? json.records
        : [];
      return { date, records };
    };

    const fetchAll = async () => {
      setDayData([]);
      setLoading(true);
      setError(null);
      try {
        const results = await Promise.all(windowDates.map(fetchDay));
        setDayData(results);
      } catch (err) {
        if (err.name === "AbortError") return;
        setError("Failed to load data. Please retry.");
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    fetchAll();
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFrom, dateTo, token, reloadTrigger, gymFilter]);

  useEffect(() => {
    setActiveCell(null);
  }, [gymFilter]);

  /**
   * gymHeadcountMap: { [gymId]: { gym_name, dates: { [date]: {
   *   trainers:     { present, absent, dayOff },
   *   receptionists:{ present, absent, dayOff },
   * } } } }
   * Each staff member is resolved at person level (any present shift = present).
   */
  const gymHeadcountMap = useMemo(() => {
    return dayData.reduce((acc, { date, records }) => {
      records.forEach((staff) => {
        const { gym_id, gym_name, account_type } = staff;
        if (!gym_id) return;
        if (!acc[gym_id]) acc[gym_id] = { gym_name, dates: {} };
        if (!acc[gym_id].dates[date]) {
          acc[gym_id].dates[date] = {
            trainers: { present: [], absent: [], dayOff: [] },
            receptionists: { present: [], absent: [], dayOff: [] },
          };
        }

        const bucket =
          account_type === "trainer"
            ? acc[gym_id].dates[date].trainers
            : acc[gym_id].dates[date].receptionists;

        const shifts = staff.shift_details || [];
        const isPresent = shifts.some(
          (s) =>
            s.attendance_details?.checkin_status === "Checked-In" ||
            s.attendance_details?.checkout_status === "checked-out"
        );
        const isDayOff =
          !isPresent &&
          shifts.length > 0 &&
          shifts.every(
            (s) =>
              s.attendance_details?.status_value === "on_leave" ||
              s.attendance_details?.status_value === "day_off"
          );

        const person = {
          user_id: staff.user_id,
          name: staff.name,
          checkin_time:
            shifts.find((s) => s.attendance_details?.checkin_time)
              ?.attendance_details?.checkin_time || null,
        };

        if (isPresent) bucket.present.push(person);
        else if (isDayOff) bucket.dayOff.push(person);
        else if (shifts.length > 0) bucket.absent.push(person);
      });
      return acc;
    }, {});
  }, [dayData]);

  const todayStr = moment().format("YYYY-MM-DD");

  const kpiData = useMemo(() => {
    let green = 0,
      yellow = 0,
      red = 0;
    Object.values(gymHeadcountMap).forEach(({ dates }) => {
      const cell = dates[todayStr];
      if (!cell) return;
      const status = getCellStatus(
        cell.trainers.present.length,
        cell.receptionists.present.length
      );
      if (status === "green") green++;
      else if (status === "yellow") yellow++;
      else red++;
    });
    return { green, yellow, red };
  }, [gymHeadcountMap, todayStr]);

  const gymIds = Object.keys(gymHeadcountMap).sort((a, b) =>
    gymHeadcountMap[a].gym_name.localeCompare(gymHeadcountMap[b].gym_name)
  );

  const goToPrevMonth = () => {
    const m = moment(`${year}-${month}`, "YYYY-M").subtract(1, "month");
    setYear(m.year());
    setMonth(m.month() + 1);
    setWindowIdx(0);
  };
  const goToNextMonth = () => {
    const m = moment(`${year}-${month}`, "YYYY-M").add(1, "month");
    if (!m.isAfter(moment(), "month")) {
      setYear(m.year());
      setMonth(m.month() + 1);
      setWindowIdx(0);
    }
  };
  const isNextDisabled = moment(`${year}-${month}`, "YYYY-M").isSameOrAfter(
    moment(),
    "month"
  );
  const monthLabel = moment(
    `${year}-${String(month).padStart(2, "0")}`,
    "YYYY-MM"
  ).format("MMMM YYYY");

  const activeCellData = activeCell
    ? gymHeadcountMap[activeCell.gymId]?.dates[activeCell.date]
    : null;

  return (
    <div className="sdt-container">
      {/* KPI row — today's snapshot */}
      <div className="shc-kpi-row">
        <div className="shc-kpi shc-kpi--green">
          <span className="shc-kpi__val">{kpiData.green}</span>
          <span className="shc-kpi__lbl">Fully Staffed Today</span>
          <span className="shc-kpi__sub">
            ≥{IDEAL_TRAINERS}T + ≥{IDEAL_RECEPTIONISTS}R
          </span>
        </div>
        <div className="shc-kpi shc-kpi--yellow">
          <span className="shc-kpi__val">{kpiData.yellow}</span>
          <span className="shc-kpi__lbl">Partially Staffed</span>
          <span className="shc-kpi__sub">Some staff present</span>
        </div>
        <div className="shc-kpi shc-kpi--red">
          <span className="shc-kpi__val">{kpiData.red}</span>
          <span className="shc-kpi__lbl">Critical Today</span>
          <span className="shc-kpi__sub">No staff present</span>
        </div>
        <div className="shc-kpi shc-kpi--blue">
          <span className="shc-kpi__val">{gymIds.length}</span>
          <span className="shc-kpi__lbl">Gyms Tracked</span>
          <span className="shc-kpi__sub">This window</span>
        </div>
      </div>

      {/* Week navigator */}
      <div className="sdm-week-nav">
        <button
          type="button"
          className="sdm-week-nav__arrow"
          onClick={goToPrevMonth}
          aria-label="Previous month"
        >
          &#8592;
        </button>
        <span className="sdm-week-nav__month">{monthLabel}</span>
        <div className="sdm-week-nav__windows">
          {windows.map((w, i) => (
            <button
              key={i}
              type="button"
              className={`sdm-week-nav__btn${
                windowIdx === i ? " sdm-week-nav__btn--active" : ""
              }`}
              onClick={() => {
                setWindowIdx(i);
                setActiveCell(null);
              }}
            >
              {w.start}–{w.end}
            </button>
          ))}
        </div>
        <button
          type="button"
          className="sdm-week-nav__arrow"
          onClick={goToNextMonth}
          disabled={isNextDisabled}
          aria-label="Next month"
        >
          &#8594;
        </button>
        <button
          type="button"
          className="sdt-date-nav__reload"
          onClick={() => setReloadTrigger((n) => n + 1)}
          aria-label="Reload"
          style={{ marginLeft: "auto" }}
        >
          ↻ Reload
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="sdm-error">
          {error}{" "}
          <button
            type="button"
            className="sdm-error__retry"
            onClick={() => setReloadTrigger((n) => n + 1)}
          >
            Retry
          </button>
        </div>
      )}

      {/* Headcount grid */}
      {!error && (
        <div className="shc-table-wrap">
          {loading ? (
            <div className="sdm-loading">Loading headcount data…</div>
          ) : gymIds.length === 0 ? (
            <div className="sdm-empty">No gym data found for this window.</div>
          ) : (
            <table className="shc-table">
              <thead>
                <tr>
                  <th className="shc-table__gym-col">Gym</th>
                  {windowDates.map((date) => (
                    <th
                      key={date}
                      className={`shc-table__day-col${
                        date === todayStr
                          ? " shc-table__day-col--today"
                          : ""
                      }`}
                    >
                      <div>{moment(date, "YYYY-MM-DD").format("ddd")}</div>
                      <div className="shc-table__day-num">
                        {moment(date, "YYYY-MM-DD").format("D")}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {gymIds.map((gymId) => {
                  const { gym_name, dates } = gymHeadcountMap[gymId];
                  return (
                    <tr key={gymId}>
                      <td
                        className="shc-table__gym-name"
                        title={gym_name}
                      >
                        {gym_name}
                      </td>
                      {windowDates.map((date) => {
                        const cell = dates[date];
                        const isFuture = date > todayStr;
                        const tp = cell?.trainers.present.length ?? 0;
                        const rp = cell?.receptionists.present.length ?? 0;
                        const status = !cell
                          ? "no-data"
                          : isFuture
                          ? "future"
                          : getCellStatus(tp, rp);
                        const isActive =
                          activeCell?.gymId === gymId &&
                          activeCell?.date === date;
                        const isClickable = !!cell && !isFuture;
                        return (
                          <td
                            key={date}
                            className={`shc-cell shc-cell--${status}${
                              isActive ? " shc-cell--active" : ""
                            }${isClickable ? " shc-cell--clickable" : ""}`}
                            onClick={() =>
                              isClickable &&
                              setActiveCell(
                                isActive
                                  ? null
                                  : { gymId, date, gymName: gym_name }
                              )
                            }
                            title={
                              isClickable
                                ? `${gym_name} — T:${tp} / R:${rp}`
                                : undefined
                            }
                          >
                            {isClickable ? (
                              <>
                                <span className="shc-cell__t">T:{tp}</span>
                                <span className="shc-cell__r">R:{rp}</span>
                              </>
                            ) : (
                              <span className="shc-cell__dash">—</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Drill-down center modal */}
      {activeCell && activeCellData && (
        <>
          <div
            className="sdm-modal-overlay"
            onClick={() => setActiveCell(null)}
            aria-hidden="true"
          />
          <div
            className="sdm-modal"
            role="dialog"
            aria-modal="true"
            aria-label={`Staff details for ${activeCell.gymName}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sdm-modal__header">
              <div>
                <div className="sdm-modal__title">{activeCell.gymName}</div>
                <div className="sdm-modal__subtitle">
                  {moment(activeCell.date, "YYYY-MM-DD").format("dddd, D MMM YYYY")}
                </div>
              </div>
              <button
                type="button"
                className="sdm-modal__close"
                onClick={() => setActiveCell(null)}
                aria-label="Close drill-down"
              >
                ✕
              </button>
            </div>
            <div className="sdm-modal__body">
              <div className="shc-drill__body">
                <DrillSection
                  title="Trainers"
                  ideal={IDEAL_TRAINERS}
                  data={activeCellData.trainers}
                />
                <DrillSection
                  title="Front Desk"
                  ideal={IDEAL_RECEPTIONISTS}
                  data={activeCellData.receptionists}
                />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default HeadcountTab;
